import SheetHeader from "../components/SheetHeader";
import { useT } from "../i18n";

/**
 * ChangeSheet is what a long press offers in the studio: the ways a line can be
 * changed (ADR-0025).
 *
 * Pronunciation used to be the only one, so the gesture opened the correction
 * sheet directly — which made fixing a spelling the dominant way to change a
 * book. A line that reads wrong is as common as one that sounds wrong, so the
 * correction is now one option beside editing the text and rewriting it.
 */
export default function ChangeSheet({
  word,
  sentence,
  onFix,
  onEdit,
  onRewrite,
  onClose,
}: {
  word: string;
  sentence: string;
  onFix: () => void;
  /** Absent where the text cannot be edited. */
  onEdit?: () => void;
  /** Absent where rewriting is not allowed. */
  onRewrite?: () => void;
  onClose: () => void;
}) {
  const t = useT();
  return (
    <div
      role="dialog"
      aria-label={t.edit.changeTitle}
      data-testid="change-sheet"
      /* Same stacking as the other sheets: above the transport and the header. */
      className="fixed inset-x-0 bottom-0 z-40 rounded-t-card border-t border-line bg-surface shadow-[0_-8px_24px_-12px_rgb(0_0_0/0.25)]"
    >
      <div className="mx-auto w-full max-w-3xl">
        <SheetHeader title={t.edit.changeTitle} onClose={onClose} closeTestID="change-close" />
        <div className="px-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
          {/* dir="auto" on the authored line and nothing inside it. */}
          <p
            dir="auto"
            data-testid="change-line"
            className="mb-3 line-clamp-3 rounded-control border-s-2 border-sage bg-sunk px-2.5 py-1.5 text-sm leading-7 text-ink-soft"
          >
            {sentence}
          </p>
          <ul className="space-y-1.5">
            {onEdit && (
              <Option testid="change-edit" onClick={onEdit}>
                {t.edit.editHere}
              </Option>
            )}
            {onRewrite && (
              <Option testid="change-rewrite" onClick={onRewrite}>
                {t.edit.rewriteHere}
              </Option>
            )}
            <Option testid="change-pronunciation" onClick={onFix}>
              {t.edit.fixPronunciation(word)}
            </Option>
          </ul>
        </div>
      </div>
    </div>
  );
}

function Option({
  testid,
  onClick,
  children,
}: {
  testid: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <li>
      <button
        type="button"
        data-testid={testid}
        onClick={onClick}
        className="w-full rounded-control border border-line px-3 py-2.5 text-start text-sm transition-colors hover:bg-sunk"
      >
        {children}
      </button>
    </li>
  );
}
